import { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { Button, Modal } from 'reactstrap'
import { SunspotLoader } from 'react-awesome-loaders'
import config from 'config'
import useTimer from 'hooks/useTimer'
import { usePutPostApi } from 'api/usePutPostApi'
import { InputField } from 'components/shared/InputField'
import { MOBILE_NUM_LENGTH, RESEND_OTP_TIME } from 'components/Result/constants'
import { filterParamsStartingWithUtm, getURLParameters } from 'utils/getAllURLParameters'
import EditIcon from 'assets/iconComp/EditIcon'

import styles from 'components/Registration/Form/signupStyles.module.css'
import { generateTruecallerLink, getExamConfig, getExamType } from 'utils/common'
import { getTruecallerPollapi, getUserData } from 'api/api'
import Loader from 'components/Loader/Loader'

const StepOneOTP = ({
    goToNextStep,
    defaultUserInfo,
    setDefaultUserInfo,
    setIsNewUser,
    setUserMethod,
    otpSent,
    setOtpSent,
    tncUrl,
    privacypolicyUrl,
}) => {
    // Autofocus function input
    useEffect(() => {
        if (window.matchMedia('(min-width: 768px)').matches) {
            const input = document.querySelector("input[name='mobile_psid']")
            input?.focus()
        }
    }, [])

    // Autofocus function input
    const queryString = window.location.href
    const url = new URL(queryString)
    const examType = getExamType()
    const parameters = getURLParameters(url.search)
    const utmParams = filterParamsStartingWithUtm(parameters)
    const [hasTrackedMain, setHasTrackedMain] = useState(false)

    const [formData, setFormData] = useState({ phone: '', mobile_psid: '', isTruecallerVerification: false })
    const [otp, setOtp] = useState(new Array(4).fill(''))
    const [receiveWhatsapp, setWhatsappComm] = useState(true)
    const [requestId, setRequestId] = useState('')
    const [editBtnEnable, setEditBtnEnable] = useState(false)

    // const [otpSent, setOtpSent] = useState(false)
    const [otpVerified, setOtpVerified] = useState(false)
    const [method, setMethod] = useState('mobile')

    const [errors, setErrors] = useState({ mobile_psid: '', otp: '' })

    // truecaller states and refs
    const [manualEntry, setManualEntry] = useState(false)
    const [polling, setPolling] = useState(false)
    const trucallerRef = useRef({
        verificationFlag: 0,
        requestId: `IACST-${Date.now()}${Math.random().toString(36).slice(2, 11)}`,
        pollcount: 0,
    })

    const trueTimeout = 24000
    const { timer, isTimerRunning, togglePlayPause, onResetTimer } = useTimer({ duration: RESEND_OTP_TIME })
 
    const {
        data: otpData,
        loading: isSendingOtp,
        error: sendOtpError,
        setPutEnabled: sendPhoneOtp,
    } = usePutPostApi(`${config.SEND_OTP_API}?mobile=${formData.mobile_psid}`, '', 'post', '', false)
 
    const {
        data: validateOtpData,
        loading: isValidatingOtp,
        error: verifyOtpError,
        setPutEnabled: validatePhoneOtp,
    } = usePutPostApi(
        `${config.VERIFY_OTP_API}?mobile_no=${formData.mobile_psid}&otp=${otp.join(
            '',
        )}&requestId=${requestId}`,
        '',
        'post',
        '',
        false,
    )

    useEffect(() => {
        sessionStorage.setItem('utmParams', JSON.stringify(utmParams))
        if (examType == "MOCK-NEET" || examType == "IAT" || examType == "MOCK-JEE") {
            if (!hasTrackedMain) {
                window.webengage.track(examType + '1_Session_Start', {
                    'utm parameters': utmParams,
                    'page_url': window.location.href,
                    'year': new Date().getFullYear(),
                    'exam_type': examType
                });
                setHasTrackedMain(true);
            }
        } else {
            window.webengage.track('iACST_0_Session_Start', {
                'utm parameters': utmParams,
            })
        }
    }, [hasTrackedMain])

    useEffect(() => {
        if (otpData?.success === true)
            onSendPhoneOtpSuccess()
        else if (otpData && !otpData.success)
            setErrors({ mobile_psid: otpData.msg, otp: '' })
    }, [otpData])

    useEffect(() => {
        if (sendOtpError)
            setErrors({ mobile_psid: sendOtpError.msg, otp: '' })

        if (verifyOtpError) {
            setErrors({ mobile_psid: '', otp: verifyOtpError.msg })

            // reset OTP field in case of invalid-otp
            if (verifyOtpError.error_code === 1)
                resetOtpBoxes()
        }
    }, [sendOtpError, verifyOtpError])

    useEffect(() => {
        if (validateOtpData?.success === true) {
            const isAttemptsExhausted =
                Number(validateOtpData.student_details[0].registration_count) ===
                validateOtpData?.allowed_registration_count

            if (isAttemptsExhausted) onUserAttemptsExhausted(formData.isTruecallerVerification)
            else onValidatePhoneOtpSuccess(validateOtpData.student_details[0])
        } else if (validateOtpData && !validateOtpData.success) {
            setErrors({ otp: validateOtpData.msg, mobile_psid: '' })

            // reset OTP field in case of invalid-otp
            if (validateOtpData.error_code === 1) resetOtpBoxes()
        }
    }, [validateOtpData])

    useEffect(() => {
        if (validateOtpData?.success && validateOtpData?.student_details?.length > 0) {
            const student = validateOtpData.student_details[0]

            if (student.student_name && student.father_name && student.student_dob && student.student_email) {
                // Perform the action when all required keys exist
                setIsNewUser(false)
            }
        }
    }, [validateOtpData])


    useEffect(() => {
        if (otpSent)
            autoFocusOtpBox()
    }, [otpSent])

    useEffect(() => {
        // restart timer countdown if OTP is sent but not verified
        if (timer !== -1 && isTimerRunning === false && otpVerified === false)
            togglePlayPause()
    }, [timer, isTimerRunning])

    const resetOtpBoxes = () => {
        setOtp(new Array(4).fill(''))

        setTimeout(() => {
            autoFocusOtpBox()
        }, 1000)
    }

    const autoFocusOtpBox = () => {
        //focus on otp field when sent and resend OTP
        const input = document.querySelector('#otp-0')
        input.focus()
    }

    const onSendPhoneOtpSuccess = () => {
        if (formData.mobile_psid.length > MOBILE_NUM_LENGTH) {
            // update mob-num when psid entered by user
            const phone = otpData.data.mobile_no
            setFormData((prevFormData) => ({
                ...prevFormData,
                phone,
            }))
        }

        onResetTimer()
        setOtpSent(true)
    }

    const onValidatePhoneOtpSuccess = ({ student_name, father_name, student_email, student_dob, mobile_no, registration_count, classstream }) => {
        setOtpVerified(true)
        onResetTimer()

        const isMobile = formData.mobile_psid.length === MOBILE_NUM_LENGTH

        if (student_name && student_dob) {
            !isMobile && setIsNewUser(false) // disabled studentInfo fields when entered PSID

            setDefaultUserInfo({
                studentName: student_name || defaultUserInfo.studentName || '',
                fatherName: father_name || '',
                email: student_email || defaultUserInfo.email || '',
                dob: new Date(student_dob) || '',
                mobileNumber: mobile_no,
                classStream: classstream || undefined,
                prevSignupAttempt: registration_count,
            })
        } else
            setDefaultUserInfo({ ...defaultUserInfo, mobileNumber: formData.mobile_psid, prevSignupAttempt: registration_count })
        // setOtpSent(false)
        goToNextStep()

        const userMobileNumber = !isMobile ? mobile_no : formData.mobile_psid

        if (!isMobile) setUserMethod({ isPsid: true, psid: formData.mobile_psid })

        if(formData.isTruecallerVerification) 
            return;

        // web-analytics
        if (examType == "MOCK-NEET" || examType == "IAT" || examType == "MOCK-JEE") {
            window.webengage.track(examType + '2_verify_OTP', {
                method: isMobile ? 'mobile' : 'psid',
                'mobile number': userMobileNumber,
                psid: isMobile ? '' : formData.mobile_psid,
                'utm parameters': utmParams,
                'page_url': window.location.href,
                'year': new Date().getFullYear(),
                'exam_type': examType,
                whatsapp_optin: true
            })
        } else {
            window.webengage.track('iACST_1_Mobile_Verification_OTP_Verified', {
                method: isMobile ? 'mobile' : 'psid',
                'mobile number': userMobileNumber,
                psid: isMobile ? '' : formData.mobile_psid,
                'utm parameters': utmParams,
            })
        }

        /**
         *  for new user: extract mobile number from input box
         *  for existing user: `mobile_no` key from API response
         */

        window.webengage.user.login(userMobileNumber)
        window.webengage.user.setAttribute('we_phone', userMobileNumber)
        window.webengage.user.setAttribute('we_whatsapp_opt_in', true)
    }

    const onUserAttemptsExhausted = (isTruecaller) => {
        const isMobile = formData.mobile_psid.length === MOBILE_NUM_LENGTH

        let registerLink = '/iacstexam'
        let supportEmail = 'iacstsupport@aesl.in'

        const examType = getExamType()
        const examConfig = getExamConfig(examType)
        if(examConfig?.commonSettings){
            registerLink = examConfig?.commonSettings?.registerPath || registerLink
            supportEmail = examConfig?.commonSettings?.supportEmail || supportEmail
        }
        
        const err = <>
            Account already exists with this mobile number. Please{' '}
            <a href="/exam/login" className="text-aqua text-decoration-none">login</a>
            {registerLink && (
                <>
                    {' '}
                    or <a href={window.location.pathname === "/iacst-exam" ? '/iacst-exam' : '/iacstexam'} className="text-aqua text-decoration-none"
                    >register</a> with a different
                    mobile number.
                </>
            )}
            {supportEmail && (
                <>{' '}For support, please write to us at{' '} <a href={`mailto:${supportEmail}`} className="text-aqua text-decoration-none">{supportEmail}</a>.</>
            )}
        </>

        setErrors({
            mobile_psid: isTruecaller ? err : "",
            otp: !isTruecaller ? err : "",
        })

        setOtpVerified(!isTruecaller)

        // Web analytics tracking
        if (examType == "MOCK-NEET" || examType == "IAT" || examType == "MOCK-JEE") {
            window.webengage.track(examType + '2_Attempts_Exhausted', {
                method: isMobile ? 'mobile' : 'psid',
                'mobile number': !isMobile ? '' : formData.mobile_psid,
                psid: isMobile ? '' : formData.mobile_psid,
                'utm parameters': utmParams,
                "page_url": window.location.href,
                "exam_type": examType
            })
        } else {
            window.webengage.track('iACST_1_Mobile_Verification_Attempts_Exhausted', {
                method: isMobile ? 'mobile' : 'psid',
                'mobile number': !isMobile ? '' : formData.mobile_psid,
                psid: isMobile ? '' : formData.mobile_psid,
                'utm parameters': utmParams,
            })
        }
    }

    const handleUpdateErrors = (name, error) => {
        setErrors((err) => ({ ...err, [name]: error }))
    }

    const handleEditMobileNum = () => {
        setFormData((prevFormData) => ({ ...prevFormData, phone: '' }))

        otpSent && setOtpSent(false)
        setEditBtnEnable(false)
        setOtp(new Array(4).fill(''))
        if (errors?.otp) {
            setErrors((preErrorStates) => {
                return {
                    ...preErrorStates,
                    otp: '',
                }
            })
        }
        setOtpVerified(false)
    }

    const editMobileNumBtn = (
        <span disabled={otpSent} className={styles.inputAddOnEditBtn}>
            <EditIcon width="20" height="20" />
        </span>
    )

    const handleFormDataChange = (event) => {
        const { name, value } = event.target

        setFormData((data) => {
            return {
                ...data,
                [name]: value,
            }
        })

        if (errors[name]) {
            setErrors((preErrorStates) => {
                return {
                    ...preErrorStates,
                    [name]: '',
                }
            })
        }
    }

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false

        if (errors?.otp) {
            setErrors((preErrorStates) => {
                return {
                    ...preErrorStates,
                    otp: '',
                }
            })
        }

        const updatedValues = otp.map((d, i) => (i === index ? element.value : d))
        setOtp([...updatedValues])

        if (element.nextSibling && element.value) {
            const input = element.nextSibling
            let end = input.value.length
            input.setSelectionRange(end, end)
            input.focus()
        } else if (!element.nextSibling && element.value && receiveWhatsapp) {
            const isOtpFilled = updatedValues.slice(0, 2).some((val) => val !== '')

            if (isOtpFilled) validatePhoneOtp(true)
        }
    }

    const autoFillOtp = (pasted, indexFocused) => {
        if (isNaN(pasted)) return false
        const otpDigits = pasted.split('')

        const digitsLeft = Math.min(otpDigits.length, otp.length - indexFocused)
        let inputNum = indexFocused,
            iterations = digitsLeft,
            i = 0

        const updatedValues = otp.map((val, index) => {
            if (index === inputNum && iterations > 0) {
                const value = otpDigits[i]
                inputNum++
                i++
                iterations--
                return value
            }
            return val
        })
        setOtp([...updatedValues])
        if (errors?.otp) {
            setErrors((preErrorStates) => {
                return {
                    ...preErrorStates,
                    otp: '',
                }
            })
        }

        const nextSibling = document.getElementById(
            `otp-${indexFocused + digitsLeft}`,
        )
        const currentValue = updatedValues[indexFocused + digitsLeft - 1]

        if (nextSibling && currentValue) {
            let end = nextSibling.value.length
            nextSibling.setSelectionRange(end, end)
            nextSibling.focus()
        } else if (!nextSibling && currentValue && receiveWhatsapp) {
            const isOtpFilled = updatedValues.slice(0, 2).some((val) => val !== '')

            if (isOtpFilled) validatePhoneOtp(true)
        }
    }

    const handleValidateOtp = (event) => {
        const isChecked = event.target.checked
        setWhatsappComm(isChecked)

        if (isChecked && !otpVerified && !otp.some((val) => val === ''))
            validatePhoneOtp(true)
    }

    const inputFocus = (event) => {
        if (event.key === 'Backspace') {
            if (event.target.previousSibling) {
                const input = event.target.previousSibling
                let end = input.value.length
                input.setSelectionRange(end, end)
                input.focus()
            }
        }
    }

    function tc_handel() {
        if (examType == "IAT" || examType == "MOCK-JEE") {
            window.webengage.track(examType + '_terms_conditions_clicked', {
                'utm parameters': utmParams,
                page_url: window.location.href,
                exam_type: examType
            })
        }
    }

    function privacy_handel() {
        if (examType == "IAT" || examType == "MOCK-JEE") {
            window.webengage.track(examType + '_privacy_policy_clicked', {
                'utm parameters': utmParams,
                page_url: window.location.href,
                exam_type: examType
            })
        }
    }

    function alreadyreg_handel() {
        if (examType == "IAT" || examType == "MOCK-JEE") {
            window.webengage.track(examType + '_login_click', {
                'utm parameters': utmParams,
                page_url: window.location.href,
                exam_type: examType
            })
        }
    }

    const handleRequestOtp = ({ isResend = false }) => {
        const isMobile = formData.mobile_psid.length === MOBILE_NUM_LENGTH

        if (formData.mobile_psid.length < MOBILE_NUM_LENGTH) return
        else if (isMobile && formData.mobile_psid[0] === '0') {
            handleUpdateErrors('mobile_psid', 'Please enter the correct number')
            return
        }

        sendPhoneOtp(true)
        setEditBtnEnable(true)

        // web-analytics
        if (!isResend)
            if (examType == "IAT" || examType == "MOCK-NEET" || examType == "MOCK-JEE") {
                const userMobileNumber = !isMobile ? "3081447061" : formData.mobile_psid
                window.webengage.track(examType + '2_Get_OTP', {
                    method: isMobile ? 'mobile' : 'psid',
                    'mobile number': !isMobile ? '' : formData.mobile_psid,
                    psid: isMobile ? '' : formData.mobile_psid,
                    'utm parameters': utmParams,
                    'exam_type': examType,
                    'page_url': window.location.href,
                    'year': new Date().getFullYear(),
                });
                if (examType == "IAT") {
                    window.webengage.user.login(userMobileNumber);
                    window.webengage.user.setAttribute('we_phone', userMobileNumber);
                    window.webengage.user.setAttribute('we_whatsapp_opt_in', true);
                }
            }
            else {
                window.webengage.track('iACST_1_Mobile_Verification_OTP_Sent', {
                    method: isMobile ? 'mobile' : 'psid',
                    'mobile number': !isMobile ? '' : formData.mobile_psid,
                    psid: isMobile ? '' : formData.mobile_psid,
                    'utm parameters': utmParams,
                })
            }
    }

    const handleResendOTPClick = () => {
        if (isSendingOtp) return
        handleRequestOtp({ isResend: true })

        setOtp(new Array(4).fill(''))
        if (errors?.otp) {
            setErrors({
                ...errors,
                otp: '',
            })
        }
        setOtpVerified(false)
        autoFocusOtpBox()

        const isMobile = formData.mobile_psid.length === MOBILE_NUM_LENGTH
        // web-analytics
        if (examType == "MOCK-NEET" || examType == "IAT" || examType == "MOCK-JEE") {
            window.webengage.track(examType + '2_Resend_OTP', {
                method: isMobile ? 'mobile' : 'psid',
                'mobile number': !isMobile ? '' : formData.mobile_psid,
                psid: isMobile ? '' : formData.mobile_psid,
                'utm parameters': utmParams,
                'page_url': window.location.href,
                'year': new Date().getFullYear(),
                'exam_type': examType
            })
        } else {
            window.webengage.track('iACST_1_Mobile_Verification_OTP_Resend', {
                method: isMobile ? 'mobile' : 'psid',
                'mobile number': !isMobile ? '' : formData.mobile_psid,
                psid: isMobile ? '' : formData.mobile_psid,
                'utm parameters': utmParams,
            })
        }
    }

    const isSendOtpBtnDisabled =
        errors.mobile_psid !== '' ||
        formData.mobile_psid.length < MOBILE_NUM_LENGTH ||
        !receiveWhatsapp

    function isMobile() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera
        // return /android|iphone|ipad|iPod/i.test(userAgent)
        return /android/i.test(userAgent)
    }

    // truecaller SDK function
    function triggerTruecallerVerification() {
        trucallerRef.current.verificationFlag = 1
        const truecallerLink = generateTruecallerLink(
            trucallerRef.current.requestId,
            trueTimeout,
        )
        window.open(truecallerLink, '_self')
        setTimeout(() => {
            if (document.hasFocus()) {
                // truecaller not present
                setManualEntry(true)
            } else {
                // truecaller is present
                setManualEntry(false)
                setPolling(true)
            }
        }, 1500)
    }
    // runs when mobile no. field is focused

    const handleFocus = () => {
        if (isMobile()) {
            if (!manualEntry && trucallerRef.current.verificationFlag === 0) {

                triggerTruecallerVerification()              
            }
        } else {
            setManualEntry(true)
        }
    }
    // polling api function
    const pollUserProfile = async (interval, poll) => {
        try {
            const response = await getTruecallerPollapi(trucallerRef.current.requestId)         
            // const mobile = response.data.data?.mobile?.length === MOBILE_NUM_LENGTH

            // Handle API response based on different status codes
            if (response.data.message === 'success' && response.data.data?.status === 'success') {
                const mobileNumber = response.data.data.mobile
                let name = `${response.data.data.firstName || ''} ${response.data.data.lastName || '' }`.trim()
                const email = response.data.data.email || ''

                // Validate mobile number format
                if (!mobileNumber || mobileNumber.length < 10) {
                    throw new Error('Invalid mobile number received')
                }

                const invalidNameChars = /[^\u0000-\u007F]|[^a-zA-Z\s]/;
                if (invalidNameChars.test(name)) {
                    name = '';
                }
  
                // const u = await getUserData(mobileNumber, examType);
                // if (u?.data.success && u?.data?.student_details?.length > 0 && u?.data?.student_details[0]?.registration_count >= u?.data?.student_details[0]?.allowed_registration_count) {
                //     setPolling(false)
                //     clearInterval(interval)
                //     setManualEntry(true)
                //     onUserAttemptsExhausted(true)
                // } else {      

                // Update form data and user info
                setFormData((data) => ({
                    ...data,
                    mobile_psid: mobileNumber,
                    phone: mobileNumber, // Ensure phone field is also updated,
                    isTruecallerVerification: true
                }))
           
                setRequestId(response.data.data.requestId)
                validatePhoneOtp(true)

                setDefaultUserInfo((data) => ({
                    ...data,
                    studentName: name,
                    email: email,
                    mobileNumber: mobileNumber,
                }))

                // Stop polling and clear interval
                setPolling(false)
                clearInterval(interval)

                // Clear any existing errors
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    mobile_psid: '',
                }))

                if (examType == "MOCK-NEET" || examType == "IAT" || examType == "MOCK-JEE") {
                    // Track analytics
                    window.webengage.track(examType + '2_Mobile_TC_Verified', {
                        'utm parameters': utmParams,
                        method: 'mobile',
                        'mobile number': mobileNumber,
                        name: name,
                        "exam_type": examType,
                        "page_url": window.location.href
                    })
                } else {
                    // Track analytics
                    window.webengage.track(
                        'iACST_1_Mobile_Verification_TC_Verified',
                        {
                            'utm parameters': utmParams,
                            method: 'mobile',
                            'mobile number': mobileNumber,
                            name: name,
                        },
                    )
                }
                // Set user attributes for analytics
                window.webengage.user.login(mobileNumber)
                window.webengage.user.setAttribute('we_phone', mobileNumber)
                window.webengage.user.setAttribute('we_whatsapp_opt_in', true)

                // Enable edit button after successful verification
                // setEditBtnEnable(true)

                // Navigate to next step
                // goToNextStep()
                return
            // }
            } else if (response.data.message === 'success' && response.data.data?.status !== 'success') {
                setPolling(false)
                clearInterval(interval)
                setManualEntry(true)

                if (examType == "MOCK-NEET" || examType == 'IAT' || examType == "MOCK-JEE") {
                    // Track failed verification attempt
                    window.webengage.track(
                        examType + '2_TC_UseAnotherMethod',
                        {
                            'utm parameters': utmParams,
                            method: 'mobile',
                            'mobile number': '',
                            "exam_type": examType,
                            "page_url": window.location.href
                        },
                    )
                }
                else {
                    // Track failed verification attempt
                    window.webengage.track(
                        'iACST_1_Mobile_Verification_TC_UseAnotherMethod',
                        {
                            'utm parameters': utmParams,
                            method: 'mobile',
                            'mobile number': '',
                        },
                    )
                }
                return
            } else {
                if (poll >= 2) {
                    console.log('Max retries reached, stopping polling.')
                    setPolling(false)
                    clearInterval(interval)
                } else {
                    console.log('No new data yet, retrying...')
                }
            }
        } catch (err) {
            console.error('Error in Truecaller verification:', err)

            // Stop polling
            setPolling(false)
            clearInterval(interval)

            // Set appropriate error message based on error type
            const errorMessage =
                err.response?.data?.message ||
                'Truecaller verification failed. Please try manual entry.'

            setErrors((prevErrors) => ({
                ...prevErrors,
                mobile_psid: errorMessage,
            }))

            // Enable manual entry as fallback
            setManualEntry(true)
        }
    }
    // useEffect for truecaller polling
    useEffect(() => {
        const time = trueTimeout / 2
        if (polling) {
            trucallerRef.current.pollcount += 1
            const interval = setInterval(() => {
                trucallerRef.current.pollcount += 1
                if (trucallerRef.current.pollcount <= 2) {
                    pollUserProfile(interval, trucallerRef.current.pollcount)
                } else {
                    setPolling(false)
                    clearInterval(interval)
                }
            }, time)
            pollUserProfile(interval, trucallerRef.current.pollcount)
            return () => {
                clearInterval(interval)
            }
        }
    }, [polling])

    return (
        <div className="position-relative p-lg-4 p-3 pt-lg-0 pt-0">
            {polling && (
                <Loader />
            )}

            {!otpSent && (
                <InputField
                    required
                    placeholder="Enter Mobile no or PSID"
                    type="tel"
                    name="mobile_psid"
                    showErrorOn="blur"
                    errorMsg={errors.mobile_psid}
                    messages={{
                        required: 'Please enter correct mobile number/ PSID',
                    }}
                    maxLength={11}
                    value={formData.mobile_psid}
                    onChange={handleFormDataChange}
                    onErrorUpdate={handleUpdateErrors}
                    // disabled={isSendingOtp}
                    // readOnly={editBtnEnable}
                    onInternalBtnClick={handleEditMobileNum}
                    // actionBtn={!editBtnEnable ? null : editMobileNumBtn}
                    onFocus={handleFocus}
                />
            )}

            {/* {otpSent && formData.mobile_psid.length > MOBILE_NUM_LENGTH && (
                <div className="font-size-xs fw-500 text-dark-grey mb-3">
                    We have sent an OTP on the below number.
                    <br />
                    {formData.mobile_psid}
                </div>
            )} */}

            {otpSent && (
                <div className={`${styles.sentnumber} mb-4`}>
                    Sent to {formData.mobile_psid}{' '}
                    <span
                        onClick={() => {
                            setOtpSent(false)
                            setErrors({ mobile_psid: '', otp: '' })
                            setOtp(new Array(4).fill(''))
                            setOtpVerified(false)
                        }}
                        className={`${styles.editbtniacst}`}
                    >
                        Edit
                    </span>
                </div>
            )}
            {/* 4 digit OTP inputs */}
            <div
                className={`d-flex flex-column w-100 ${otpSent ? 'd-flex' : 'd-none'
                    }`}
            >
                <div
                    className={cx('d-flex', {
                        '': errors.otp,
                    })}
                >
                    {otp.map((data, index) => {
                        return (
                            <input
                                key={index}
                                autoComplete="off"
                                placeholder="-"
                                name="otp"
                                id={`otp-${index}`}
                                pattern="[0-9]{1}"
                                inputMode="numeric"
                                className={cx(styles.otpInputBox, {
                                    'border-primary': data,
                                    'border-error-red': !!errors.otp,
                                    'border-dark-green': otpVerified,
                                })}
                                type="text"
                                tabIndex={index + 1}
                                // maxLength={1}
                                value={data}
                                onChange={(e) => {
                                    if (e.target.value.length > 1)
                                        autoFillOtp(e.target.value, index)
                                    else handleOtpChange(e.target, index)
                                }}
                                onFocus={(e) => e?.target?.select()}
                                onKeyUp={(e) => inputFocus(e)}
                                disabled={otpVerified || isValidatingOtp}
                            />
                        )
                    })}
                </div>

                {errors?.otp && (
                    <span className="mb-2 font-size-xs text-error-red fw-500">
                        {errors.otp}
                    </span>
                )}
                {
                    useEffect(() => {
                        if (errors?.otp) {
                            // Track error via webengage only if OTP error exists
                            if (examType == "MOCK-NEET" || examType == "IAT" || examType == "MOCK-JEE") {
                                window.webengage.track(examType + '2_Error_OTP', {
                                    method: isMobile ? 'mobile' : 'psid',
                                    'mobile number':
                                        formData.mobile_psid.length ===
                                            MOBILE_NUM_LENGTH
                                            ? formData.mobile_psid
                                            : '',
                                    psid:
                                        formData.mobile_psid.length ===
                                            MOBILE_NUM_LENGTH
                                            ? ''
                                            : formData.mobile_psid,
                                    'utm parameters': utmParams,
                                    page_url: window.location.href,
                                    year: new Date().getFullYear(),
                                    exam_type: examType,
                                })
                            }
                        }
                    }, [errors]) // Only triggers when errors change
                }

                <div className="ml-auto">
                    {isTimerRunning && !otpVerified && (
                        <div className="fw-500 font-size-xs text-dark-grey">
                            Resend OTP in{' '}
                            <span className="text-primary">
                                0{Math.floor(timer / 60)}:
                                {timer.toString().padStart(2, '0')} seconds
                            </span>
                        </div>
                    )}
                    {!isTimerRunning && !otpVerified && (
                        <div
                            className={cx(
                                'text-primary cursor-pointer fw-500 font-size-xs',
                                {
                                    'text-grey-50': isSendingOtp,
                                },
                            )}
                            onClick={handleResendOTPClick}
                        >
                            Resend OTP
                        </div>
                    )}
                </div>
            </div>
            {/* Sent OTP Button */}
            <Button
                className={cx(
                    `btn btn-aqua text-white w-100 fw-700 font-size-mds font-size-lg-lg mt-2 rounded ${styles.reg_custom_btn} ${styles.sendOtpButton}`,
                    { 'bg-grey-20 text-grey-70 border-0': isSendOtpBtnDisabled },
                    { 'd-none': otpSent },
                )}
                onClick={handleRequestOtp}
                disabled={isSendOtpBtnDisabled}
            >
                Verify
            </Button>
            <div className={`${styles.alreadyreg} w-full text-left mt-3`}>
                <p className="text-left font-size-lg-md font-size-xs mb-0 text-grey-llight">
                    Already registered ?&nbsp;
                    <a
                        href="/exam/login"
                        className="text-decoration-none text-aqua fw-lighter font-size-lg-md font-size-sm"
                        onClick={alreadyreg_handel}
                    >
                        Log in
                    </a>
                </p>
            </div>
            {/* whatsapp opt-in */}
            <div className="mt-3 mx-auto d-flex align-items-start">
                {/* <input
                    className={styles.whatsAppOptinCheckBox}
                    type="checkbox"
                    checked={receiveWhatsapp}
                    onChange={(e) => {
                        if (otpSent) handleValidateOtp(e)
                        else setWhatsappComm(!receiveWhatsapp)
                    }}
                />{' '} */}
                <p
                    className={`${styles.privacytext} mb-0 font-size-11 text-grey lh-16`}
                >
                    By proceeding, you agree to Aakash’s{' '}
                    <a
                        // href={PRIVACY_POLICY}
                        href={privacypolicyUrl}
                        onClick={privacy_handel}
                        target="_blank"
                        rel="noreferrer"
                        className="text-aqua text-decoration-none"
                    >
                        Privacy Policy &nbsp;
                    </a>
                    and
                    <a
                        href={tncUrl}
                        onClick={tc_handel}
                        target="_blank"
                        rel="noreferrer"
                        className="text-aqua text-decoration-none"
                    >
                        &nbsp; T&C{' '}
                        <span className="text-star-red font-size-xs fw-lighter">
                            *
                        </span>
                    </a>
                    {/* {!receiveWhatsapp && (
                        <section
                            className={cx('mt-1 font-size-xs text-error-red', {
                                'fw-500': otpSent,
                            })}
                        >
                            Please accept T&C in order to continue
                        </section>
                    )} */}
                </p>
            </div>

            {(isValidatingOtp || isSendingOtp) && (
                <Modal
                    isOpen={true}
                    backdrop={true}
                    centered
                    className={styles.customModalLoader}
                >
                    <SunspotLoader
                        gradientColors={['#4039d4', '#E0E7FF']}
                        size={'10px'}
                        desktopSize={'30px'}
                        mobileSize={'20px'}
                        className="m-auto"
                    />
                </Modal>
            )}
        </div>
    )
}

export default StepOneOTP
